import { useDeferredValue, useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Form, FormField } from '@/components/ui/form';
import {
  PlannerConfigEntityWithOptionalFields,
  TEAM_VALUES,
} from '@/services/configurator/PlannerConfigTypes';
import { PlannerFormItemImage } from '../planner-item/planner-form-item-image';
import { PlannerFormItemMarketData } from '../planner-item/PlannerFormListMarketData/planner-form-item-market-data';
import {
  REGEX_PATTERN_NUMBER,
  REGEX_PATTERN_NUMBER_OR_STRING,
} from '@/utils/regex';
import { useUser } from '@/components/providers/user-provider';
import { EDIT_CONFIGURATION_PERMISSIONS } from '@/config/feature-permissions';
import { useEnv } from '@/components/providers/env-provider';
import { hasPermission } from '@/utils/validation';
import {
  contentPageVariantsArray,
  showInPagesArray,
  supportedDevicesArray,
} from '@/types/PlannerFormSchema';
import { isProdTabActive } from '@/shared-utils/utils';
import { getCategoryItems } from '@/utils/categories';
import { Mode } from '../planner/PlannerPageContainer';
import FormInputComponent from './formComponent/FormInputComponent';
import FormSelectComponent from './formComponent/FormSelectComponent';
import FormCheckboxComponent from './formComponent/FormCheckboxComponent';
import FormRadioGroupComponent from './formComponent/FormRadioGroupComponent';
import { MultiSelectCombobox } from './formComponent/FormMultiSelect';
import FormPillInputComponent from './formComponent/PillInput/FormPillInputComponent';
import { normalizedValue } from '@/utils/normalizedValue';
import { Lock } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ReactNode } from 'react';
import { ConfiguratorApi, CollidingCategory } from '@/utils/ConfiguratorApi';

export type ConfigFormProps = {
  form: UseFormReturn<PlannerConfigEntityWithOptionalFields>;
  initialState: PlannerConfigEntityWithOptionalFields;
  mode: Mode;
  disabledFormFields: boolean;
  onCollisionChange?: (hasCollision: boolean) => void;
};

export function ConfigForm({
  form,
  initialState,
  mode,
  disabledFormFields,
  onCollisionChange,
}: ConfigFormProps) {
  const { role, editConfigEnabled, restrictions } = useUser();
  const { env } = useEnv();
  const isProd = isProdTabActive(env);

  const isRestrictedUser = restrictions?.markets && mode !== 'create';

  const isFieldDisabled = (
    name: keyof PlannerConfigEntityWithOptionalFields,
  ) => {
    return (
      disabledFormFields ||
      !hasPermission(
        EDIT_CONFIGURATION_PERMISSIONS[name][env],
        role,
        editConfigEnabled,
      )
    );
  };

  const restrictedLabel = (label: string): ReactNode => {
    if (isRestrictedUser) {
      return (
        <span className='flex items-center gap-2'>
          {label}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Lock className='h-4 w-4 text-muted-foreground' />
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  This field is restricted. Only market-specific settings can be
                  edited.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </span>
      );
    }
    return label;
  };

  const watchedShowInPages = form.watch('showInPages');
  const watchedMarketData = form.watch('marketData');
  const watchedIdentifier = form.watch('identifier');
  const watchedContentPageVariant = form.watch('contentPageVariant');
  const watchedTeam = form.watch('team');
  const identifierFieldDisabled = mode === 'edit';

  const isKreativGeneral = watchedIdentifier === 'kreativ-general';
  const isKreativTeam = watchedTeam === 'kreativ';

  useEffect(() => {
    if (watchedShowInPages?.includes('content-page')) {
      form.trigger();
    }
    if (watchedContentPageVariant) {
      form.trigger().then(() => {});
    }
  }, [watchedShowInPages, watchedContentPageVariant, form]);

  const [collidingCategories, setCollidingCategories] = useState<
    CollidingCategory[]
  >([]);

  const watchedRangeInclude = form.watch('rangeInclude');

  const activeCategories = normalizedValue(watchedRangeInclude);
  const activeLocales = (watchedMarketData ?? [])
    .filter((m) => m.isActive)
    .map((m) => m.locale);

  const plannerId = initialState.id;
  const deferredActiveCategories = useDeferredValue(activeCategories.join(','));
  const deferredActiveLocales = useDeferredValue(activeLocales.join(','));

  useEffect(() => {
    const plannerActiveCategories = normalizedValue(deferredActiveCategories);
    const plannerActiveLocales = normalizedValue(deferredActiveLocales);

    if (!plannerActiveCategories.length || !plannerActiveLocales.length) {
      setCollidingCategories([]);
      onCollisionChange?.(false);
      return;
    }

    let cancelled = false;

    ConfiguratorApi.getInstance()
      .getCollidingCategories(
        env,
        plannerId,
        plannerActiveCategories,
        plannerActiveLocales,
      )
      .then((result) => {
        if (!cancelled) {
          setCollidingCategories(result);
          onCollisionChange?.(result.length > 0);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Unable to list published planners', error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    deferredActiveCategories,
    deferredActiveLocales,
    env,
    plannerId,
    onCollisionChange,
  ]);

  return (
    <Form {...form}>
      <form className='grid grid-cols-2 gap-8'>
        <div className='col-span-2 grid grid-cols-3 gap-8 border-solid border-2 rounded p-4'>
          <FormInputComponent
            formControl={form.control}
            name='identifier'
            formLabel='Identifier'
            formDescription='Unique identifier for the configuration, that is created when the planner is added for the first time.'
            disabled={identifierFieldDisabled || isRestrictedUser}
          />
        </div>
        <div className='col-span-2 grid grid-cols-3 gap-8 border-solid border-2 rounded p-4'>
          {mode === 'edit' ? (
            <FormInputComponent
              formControl={form.control}
              name='team'
              formLabel='Team'
              formDescription='Team responsible for this planner'
              disabled={isRestrictedUser}
            />
          ) : (
            <FormRadioGroupComponent
              formControl={form.control}
              name='team'
              formLabel='Team'
              radioItems={TEAM_VALUES}
            />
          )}
          <FormInputComponent
            formControl={form.control}
            name='plannerContact'
            formLabel={restrictedLabel('Planner Contact')}
            formDescription='Contact person for this planner'
            disabled={isFieldDisabled('plannerContact') || isRestrictedUser}
          />
        </div>
        <FormField
          control={form.control}
          name='marketData'
          render={({ field }) => {
            return (
              <PlannerFormItemMarketData
                form={form}
                dynamicMarketData={watchedMarketData}
                field={field}
                marketData={field.value || []}
                initialState={initialState}
                onMarketDataChange={(value) => {
                  field.onChange(value);
                }}
                disabled={isFieldDisabled('marketData')}
                disableIdFields={isKreativGeneral}
              />
            );
          }}
        />
        <div className='col-span-2 grid grid-cols-2 gap-8 border-solid border-1 rounded p-4'>
          <FormCheckboxComponent
            formControl={form.control}
            name='searchable'
            formLabel={restrictedLabel('Searchable')}
            formDescription='Disables/enables from being found in search function. Please provide the search keywords in the translation tab'
            disabled={isFieldDisabled('searchable') || isRestrictedUser}
          />
          {hasPermission(
            EDIT_CONFIGURATION_PERMISSIONS.includeListingSearchParams[env],
            role,
          ) && (
            <FormCheckboxComponent
              formControl={form.control}
              name='includeListingSearchParams'
              formLabel='Include Listing search parameters'
              formDescription='Add the category ID or the search query as a query param to the planner URL on PLP pages.'
              disabled={disabledFormFields || isRestrictedUser}
            />
          )}
          {hasPermission(
            EDIT_CONFIGURATION_PERMISSIONS.includeDeeplinkParams[env],
            role,
          ) && (
            <FormCheckboxComponent
              formControl={form.control}
              name='includeDeeplinkParams'
              formLabel='Include DeepLink parameters'
              formDescription='Add the product ID as a query param and hash to the planner URL on PIP pages. (?productId=<product-id>#<product-id>)'
              disabled={disabledFormFields || isRestrictedUser}
            />
          )}
        </div>
        <div className='col-span-2 grid grid-cols-1 min-[800px]:grid-cols-2 gap-8 border-solid border-1 rounded p-4'>
          <FormField
            control={form.control}
            name='imageUrl'
            render={({ field }) => (
              <PlannerFormItemImage
                field={field}
                label={restrictedLabel('Image Url')}
                description='Primary planner image url (Used for banner-large and content-page)'
                disabled={isFieldDisabled('imageUrl') || isRestrictedUser}
              />
            )}
          />
          <FormInputComponent
            formControl={form.control}
            name='plannerUrl'
            formLabel={restrictedLabel('Planner Url')}
            formDescription='The url to planner.'
            disabled={isFieldDisabled('plannerUrl') || isRestrictedUser}
          />
          <FormField
            control={form.control}
            name='secondaryBannerImgUrl'
            render={({ field }) => (
              <PlannerFormItemImage
                field={field}
                label={restrictedLabel('Secondary image url')}
                description='Used on content page that has a secondary banner after the main banner. More specific banners_with_video_and_external_link and banners_with_external_link. '
                disabled={
                  isFieldDisabled('secondaryBannerImgUrl') || isRestrictedUser
                }
              />
            )}
          />
        </div>
        <div className='col-span-2 grid grid-cols-2 max-[1200px]:grid-cols-1 gap-8 border-solid border-1 rounded p-4'>
          <FormSelectComponent
            formControl={form.control}
            name='category'
            selectValuePlaceholder='Set planner category'
            selectContent={getCategoryItems(isKreativTeam)}
            formDescription='What category does this planner belong to. Used to group the planners on ikea.com/xx/yy/planners'
            disabled={
              isFieldDisabled('category') ||
              isKreativTeam ||
              isProd ||
              isRestrictedUser
            }
          />
          <FormField
            control={form.control}
            name='supportedDevices'
            render={({ field }) => (
              <MultiSelectCombobox
                items={supportedDevicesArray}
                selectedValues={field.value || []}
                onSelectionChange={field.onChange}
                placeholder='Show in pages'
                description='What devices planner is active in.'
                disabled={
                  isFieldDisabled('supportedDevices') || isRestrictedUser
                }
              />
            )}
          />
          <FormField
            control={form.control}
            name='showInPages'
            render={({ field }) => (
              <MultiSelectCombobox
                items={showInPagesArray}
                selectedValues={field.value || []}
                onSelectionChange={field.onChange}
                placeholder='Show in pages'
                description='What pages should the planner be active in.'
                disabled={isFieldDisabled('showInPages') || isRestrictedUser}
              />
            )}
          />
          <div
            className={`${watchedShowInPages?.includes('content-page') ? 'visible' : 'invisible'}`}
          >
            <FormSelectComponent
              formControl={form.control}
              name='contentPageVariant'
              selectValuePlaceholder='Set content page variation'
              selectContent={contentPageVariantsArray}
              formDescription='What style to show in planner content page, e.g iframe.'
              disabled={
                isFieldDisabled('contentPageVariant') ||
                isProd ||
                isRestrictedUser
              }
            />
          </div>
        </div>
        <div className='col-span-2 grid grid-cols-1 min-[1200px]:grid-cols-2  gap-8'>
          <FormPillInputComponent
            formControl={form.control}
            name='productsInclude'
            regexpValidation={REGEX_PATTERN_NUMBER}
            formLabel={restrictedLabel('Products include')}
            formDescription='What product planner should be active in'
            placeholder='Enter product id'
            disabled={
              isFieldDisabled('productsInclude') ||
              isKreativGeneral ||
              isRestrictedUser
            }
          />
          <FormPillInputComponent
            formControl={form.control}
            name='productsExclude'
            regexpValidation={REGEX_PATTERN_NUMBER}
            formLabel={restrictedLabel('Products exclude')}
            formDescription='What product planner should NOT be active in'
            placeholder='Enter product id'
            disabled={
              isFieldDisabled('productsExclude') ||
              isKreativGeneral ||
              isRestrictedUser
            }
          />
        </div>
        <div className='col-span-2 grid grid-cols-1 min-[1200px]:grid-cols-2 gap-8'>
          <FormPillInputComponent
            formControl={form.control}
            name='rangeInclude'
            regexpValidation={REGEX_PATTERN_NUMBER_OR_STRING}
            formLabel={restrictedLabel('Categories includes')}
            formDescription='What category planner should be active in'
            placeholder='Enter category id'
            disabled={
              isFieldDisabled('rangeInclude') ||
              isKreativGeneral ||
              isRestrictedUser
            }
            formMessage={
              collidingCategories.length > 0 ? (
                <>
                  <span>
                    You have overlapping categories with other planners. This
                    might cause issues and result in planners overwriting each
                    other:
                  </span>
                  <br />
                  <ul className='list-disc pl-4'>
                    {collidingCategories.map((item) => (
                      <li key={item.rangeInclude} className='each'>
                        {item.rangeInclude} - {item.plannerIds.join(', ')} -
                        Locales: {item.locales.join(', ')}
                      </li>
                    ))}
                  </ul>
                </>
              ) : undefined
            }
          />
          <FormPillInputComponent
            formControl={form.control}
            name='rangeExclude'
            regexpValidation={REGEX_PATTERN_NUMBER_OR_STRING}
            formLabel={restrictedLabel('Categories excludes (Legacy)')}
            formDescription='What category planner should NOT be active in'
            placeholder='Enter category id'
            disabled={
              isFieldDisabled('rangeExclude') ||
              isKreativGeneral ||
              isRestrictedUser
            }
          />
        </div>
      </form>
    </Form>
  );
}
